import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button, Card, CardBody, Input, Skeleton, useDisclosure } from '@nextui-org/react';
import {
  ArrowLeft,
  ChevronDown,
  LogIn,
  Plus,
  Search,
  Swords,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import lobbyService, { Lobby } from '../services/lobbyService';
import LobbyCard from '../components/community/LobbyCard';
import LobbyFormModal from '../components/community/LobbyFormModal';

const ACCENT = '#3fced1';
const PAGE_SIZE = 12;

const STATUS_FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'Alle' },
  { key: 'open', label: 'Offen' },
  { key: 'full', label: 'Voll' },
  { key: 'closed', label: 'Geschlossen' },
];

const LobbiesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const createModal = useDisclosure();

  const [lobbies, setLobbies] = useState<Lobby[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLoginHint, setShowLoginHint] = useState(false);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Request-Versionierung gegen Race-Conditions bei schnellen Filterwechseln
  const requestIdRef = useRef(0);

  const loadLobbies = useCallback(
    async (offset: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await lobbyService.getLobbies({
          limit: PAGE_SIZE,
          offset,
          status: statusFilter || undefined,
          search: search || undefined,
        });
        if (requestIdRef.current !== requestId) return;
        setLobbies((prev) => (append ? [...prev, ...res.lobbies] : res.lobbies));
        setTotal(res.total);
        setError(null);
      } catch (err: any) {
        if (requestIdRef.current !== requestId) return;
        setError(
          lobbyService.extractErrorMessage(err, 'Lobbies konnten nicht geladen werden.')
        );
        if (!append) {
          setLobbies([]);
          setTotal(0);
        }
      } finally {
        if (requestIdRef.current === requestId) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [search, statusFilter]
  );

  // Initial + bei Filterwechsel (Suche debounced)
  useEffect(() => {
    const timer = setTimeout(() => loadLobbies(0, false), search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [loadLobbies, search]);

  const hasMore = lobbies.length < total;

  const openCreateModal = () => {
    if (!isAuthenticated) {
      setShowLoginHint(true);
      return;
    }
    setShowLoginHint(false);
    createModal.onOpen();
  };

  const onLobbyCreated = (lobby: Lobby) => {
    if (lobby.id) {
      navigate(`/community/lobby/${encodeURIComponent(lobby.id)}`);
    } else {
      loadLobbies(0, false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Spiel-Lobbies - Finde Mitspieler | DartCloud</title>
        <meta
          name="description"
          content="Finde Mitspieler in den DartCloud Spiel-Lobbies: 501, Cricket, Training und mehr. Erstelle deine eigene Lobby oder tritt einer offenen Runde bei."
        />
      </Helmet>

      <div className="min-h-screen bg-zinc-950">
        {/* ============ Hero ============ */}
        <section className="relative overflow-hidden border-b border-zinc-800/80">
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950" />
          <div
            className="absolute -top-32 left-1/2 -translate-x-1/2 w-[640px] h-[320px] rounded-full blur-3xl opacity-20 pointer-events-none"
            style={{ backgroundColor: ACCENT }}
          />

          <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <RouterLink
              to="/community"
              className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mb-6"
            >
              <ArrowLeft size={16} />
              Zur Community
            </RouterLink>

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <div
                  className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border text-xs font-semibold mb-4"
                  style={{
                    borderColor: `${ACCENT}40`,
                    backgroundColor: `${ACCENT}14`,
                    color: ACCENT,
                  }}
                >
                  <Swords size={14} />
                  Spiel-Lobbies
                </div>
                <h1 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight mb-3">
                  Finde deine{' '}
                  <span
                    className="text-transparent bg-clip-text"
                    style={{ backgroundImage: `linear-gradient(90deg, ${ACCENT}, #7de8ea)` }}
                  >
                    nächste Runde.
                  </span>
                </h1>
                <p className="text-sm md:text-base text-gray-400 max-w-xl leading-relaxed">
                  Tritt einer offenen Lobby bei oder erstelle deine eigene — 501, Cricket,
                  Training und mehr. Gemeinsam macht Darts einfach mehr Spaß.
                </p>
              </div>

              <Button
                size="lg"
                className="font-semibold text-zinc-950 flex-shrink-0"
                style={{ backgroundColor: ACCENT }}
                startContent={<Plus size={18} />}
                onPress={openCreateModal}
              >
                Lobby erstellen
              </Button>
            </div>

            {showLoginHint && !isAuthenticated && (
              <div className="mt-5 flex items-start justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-900/80 px-4 py-3">
                <p className="text-sm text-gray-300">
                  <RouterLink
                    to="/login"
                    className="font-semibold hover:underline"
                    style={{ color: ACCENT }}
                  >
                    Melde dich an
                  </RouterLink>
                  , um eine eigene Lobby zu erstellen.
                </p>
                <button
                  type="button"
                  onClick={() => setShowLoginHint(false)}
                  className="text-gray-500 hover:text-white flex-shrink-0"
                  aria-label="Hinweis schließen"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* ============ Filter + Grid ============ */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
          {/* Suchfeld + Status-Chips */}
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
            <Input
              value={search}
              onValueChange={setSearch}
              placeholder="Lobby suchen …"
              startContent={<Search size={16} className="text-gray-500" />}
              isClearable
              onClear={() => setSearch('')}
              classNames={{
                base: 'md:max-w-xs',
                inputWrapper: 'bg-zinc-900 border border-zinc-800',
                input: 'text-white placeholder:text-gray-500',
              }}
            />
            <div className="flex items-center gap-2 flex-wrap">
              {STATUS_FILTERS.map((filter) => {
                const active = statusFilter === filter.key;
                return (
                  <button
                    key={filter.key || 'all'}
                    type="button"
                    onClick={() => setStatusFilter(filter.key)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                      active
                        ? 'text-zinc-950'
                        : 'text-gray-400 border-zinc-800 bg-zinc-900 hover:text-white hover:border-zinc-700'
                    }`}
                    style={
                      active
                        ? { backgroundColor: ACCENT, borderColor: ACCENT }
                        : undefined
                    }
                  >
                    {filter.label}
                  </button>
                );
              })}
            </div>
            {total > 0 && !loading && (
              <span className="text-xs text-gray-500 md:ml-auto">
                {total.toLocaleString('de-DE')} {total === 1 ? 'Lobby' : 'Lobbies'}
              </span>
            )}
          </div>

          {error && (
            <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="bg-zinc-900 border border-zinc-800" shadow="sm">
                  <CardBody className="p-4 space-y-3">
                    <Skeleton className="h-4 w-20 rounded-full bg-zinc-800" />
                    <Skeleton className="h-5 w-3/4 rounded-md bg-zinc-800" />
                    <Skeleton className="h-3 w-full rounded-md bg-zinc-800" />
                    <Skeleton className="h-6 w-2/3 rounded-md bg-zinc-800" />
                  </CardBody>
                </Card>
              ))}
            </div>
          ) : lobbies.length === 0 ? (
            <div className="text-center py-20 rounded-2xl border border-dashed border-zinc-800">
              <div
                className="inline-flex p-4 rounded-2xl mb-4"
                style={{ backgroundColor: `${ACCENT}14` }}
              >
                <Swords size={32} style={{ color: ACCENT }} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                {search || statusFilter
                  ? 'Keine Lobbies gefunden'
                  : 'Noch keine Lobbies vorhanden'}
              </h3>
              <p className="text-sm text-gray-400 max-w-sm mx-auto mb-6">
                {search || statusFilter
                  ? 'Passe deine Suche oder Filter an — oder erstelle einfach selbst eine Lobby.'
                  : 'Sei der Erste und eröffne eine Lobby für deine nächste Runde.'}
              </p>
              {isAuthenticated ? (
                <Button
                  className="font-semibold text-zinc-950"
                  style={{ backgroundColor: ACCENT }}
                  startContent={<Plus size={16} />}
                  onPress={openCreateModal}
                >
                  Erste Lobby erstellen
                </Button>
              ) : (
                <Button
                  as={RouterLink}
                  to="/login"
                  variant="bordered"
                  className="font-semibold text-white border-zinc-700"
                  startContent={<LogIn size={16} />}
                >
                  Anmelden und loslegen
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {lobbies.map((lobby, idx) => (
                  <LobbyCard key={lobby.id ?? idx} lobby={lobby} />
                ))}
              </div>

              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <Button
                    variant="bordered"
                    className="font-semibold text-white border-zinc-700"
                    startContent={<ChevronDown size={16} />}
                    isLoading={loadingMore}
                    onPress={() => loadLobbies(lobbies.length, true)}
                  >
                    Mehr laden
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <LobbyFormModal
        isOpen={createModal.isOpen}
        onOpenChange={createModal.onOpenChange}
        onSaved={onLobbyCreated}
      />
    </>
  );
};

export default LobbiesPage;
